import { ClientMailUrl } from "../helpers/config";
import { GetLocalAuthToken, HandleExpiredToken } from "./MoblyzeAuth";

// Thin client for the moblyze-client-mail Worker's service-account routes.
// Requests carry the portal's own JWT; the Worker re-checks that the caller is
// a Moblyze super user. Errors come back as `{ error: "<string>" }` and are
// thrown as ClientMailError with that message and the HTTP status.

export class ClientMailError extends Error {
  constructor(message, status) {
    super(message);
    this.name = "ClientMailError";
    this.status = status;
  }
}

async function request(path, { method = "GET", body } = {}) {
  const token = await GetLocalAuthToken();
  const headers = { Authorization: token ? `Bearer ${token}` : "" };
  if (body !== undefined) headers["Content-Type"] = "application/json";

  let response;
  try {
    response = await fetch(`${ClientMailUrl}${path}`, {
      method,
      headers,
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  } catch {
    throw new ClientMailError("Could not reach the mail service.", 0);
  }

  let result = null;
  try {
    result = await response.json();
  } catch {
    // Non-JSON body: fall through to the status-based message.
  }

  if (!response.ok) {
    // 401: the session expired — sign out as everywhere else. 403 means "not
    // a super user" and must not sign anyone out.
    if (response.status === 401) HandleExpiredToken();
    throw new ClientMailError(
      result?.error || `Request failed (${response.status})`,
      response.status
    );
  }
  return result;
}

export function listServiceAccounts() {
  return request("/v1/service-accounts");
}

// What a service-account mailbox is connected for: sending, worker-mediated
// inbox operations, and raw Graph tokens for the outreach jobs in
// moblyze-ops. The mail service caps a service account at exactly these.
export const SERVICE_ACCOUNT_GRANTS = ["SEND", "MANAGE_INBOX", "RAW_ACCESS"];

export function startServiceAccountConnect({ serviceAccount, expectedMailbox, returnPath }) {
  return request("/v1/connect", {
    method: "POST",
    body: { provider: "M365", grants: SERVICE_ACCOUNT_GRANTS, serviceAccount, expectedMailbox, returnPath },
  });
}

export function disconnectConnection(id) {
  return request(`/v1/connections/${encodeURIComponent(id)}`, { method: "DELETE" });
}
